import { ref } from "vue";
import type { Maker } from "~/types";
import { isVisibleMaker } from "~/utils/isVisibleMaker";
import { searchWords, matchesWords } from "~/utils/textSearch";

// Shared deepSearch logic for both the navbar SearchBar dropdown and the
// /search results page (via the makers store). Previously each consumer
// re-implemented local matching + fetch + merge with no request cancellation,
// so a slow response for an earlier keystroke could land after a newer one and
// overwrite the results — the "dropdown sometimes doesn't appear" bug.
//
// runDeepSearch aborts any in-flight request on each new call and tags every
// result with the term that produced it; a result for a superseded term resolves
// to `null` so the caller ignores it. Each call site gets its own instance
// (independent abort controller + loading flag), which is correct — the dropdown
// and the results page are independent search contexts.
//
// The backend (makerSearchService) is the source of truth for a *successful*
// search: maker name + maker description + product name + attribute-tag name,
// word-prefix, stopword-aware FULLTEXT. The client-side localMatches is a fallback for a
// *failed* request only — for name, description and product it mirrors the same
// contract so it can't surface makers the live search deliberately excluded.
// Attribute tags are looser here: the backend anchors a single-word term to the
// start of the tag name, while this word-prefix matches any word in it, so
// "owned" matches every Owned tag here and none on the backend. (It previously substring-matched
// and was merged into every success, which re-added almost every maker for common
// terms like "the"; word-prefix + stopword-aware matching is what makes including
// the maker description safe now.)

export function useDeepSearch() {
  const config = useRuntimeConfig();

  // Loading flag for the current (latest) search. Drives the dropdown spinner.
  const isSearching = ref(false);

  let controller: AbortController | null = null;
  let latestTerm = "";

  // Client-side matches against the already-loaded maker categories, used only
  // when the API request fails. Mirrors the backend contract: maker name + maker
  // description + product name, word-prefix, stopword-aware — never substrings.
  // Attribute-tag names are matched the same way, which is looser than the backend.
  function localMatches(term: string): Maker[] {
    const words = searchWords(term);
    if (words.length === 0) return [];

    const makersStore = useMakersStore();
    const authStore = useAuthStore();
    const userEmail = authStore.user?.email || "";
    const matches: Maker[] = [];
    const categories = makersStore.makerCategories;

    if (Array.isArray(categories)) {
      const seen = new Set<string | number>();
      categories.forEach((cat) => {
        (cat.makers || []).forEach((maker) => {
          if (seen.has(maker.id) || maker.is_active === false) return;
          if (!isVisibleMaker(maker.id, userEmail)) return;
          const nameMatch = matchesWords(maker.name, words);
          const descMatch = matchesWords(maker.description || "", words);
          const productMatch = maker.products
            ?.filter((p) => (p as Record<string, unknown>).is_active !== false)
            .some((p) => matchesWords(p.name, words));
          const attributeTagMatch = maker.tags
            ?.filter((tag) => tag.category === "attribute")
            .some((tag) => matchesWords(tag.name, words));
          if (nameMatch || descMatch || productMatch || attributeTagMatch) {
            seen.add(maker.id);
            matches.push(maker);
          }
        });
      });
    }
    return matches;
  }

  /**
   * Run a deepSearch. Returns the visible-filtered makers, or `null` if this
   * call was superseded by a newer term (caller should ignore a null).
   */
  async function runDeepSearch(term: string): Promise<Maker[] | null> {
    if (!term || term.length <= 2) {
      latestTerm = term;
      return [];
    }

    latestTerm = term;
    // Cancel the previous in-flight request — its result is now stale.
    controller?.abort();
    controller = new AbortController();
    isSearching.value = true;

    try {
      const response = await $fetch<Maker[]>(
        `${config.public.apiBase}/api/public/makers/deepSearch?searchName=${encodeURIComponent(term)}&is_biz=true`,
        { signal: controller.signal },
      );

      // A newer keystroke superseded this request between dispatch and response.
      if (term !== latestTerm) return null;

      // The backend is authoritative — return exactly what it matched, visible-
      // filtered. Do NOT pad with localMatches: that re-added makers the backend
      // deliberately excluded. The deepSearch response carries no is_active field
      // (the backend already returns only active makers), so filter leniently with
      // `!== false` — a strict truthy check would drop every maker.
      const authStore = useAuthStore();
      const userEmail = authStore.user?.email || "";
      return response.filter((m) => m.is_active !== false && isVisibleMaker(m.id, userEmail));
    } catch (error) {
      // Aborted requests are expected when typing quickly — swallow them.
      if ((error as Error)?.name === "AbortError" || term !== latestTerm) return null;
      // Genuine failure: fall back to whatever we matched locally.
      return localMatches(term);
    } finally {
      // Only the latest search owns the spinner.
      if (term === latestTerm) isSearching.value = false;
    }
  }

  return { runDeepSearch, isSearching };
}
